import { ElementRef } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import { DomHandler } from "@bfx/primeng/dom";
import { FwdRollAllocationModel } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/FwdRollAllocationModel";
import { FwdRollChangeBuffer } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/FwdRollChangeBuffer";
import { FwdRollCurrencyModel } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/FwdRollCurrencyModel";
import { FwdRollDealtPairModel } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/FwdRollDealtPairModel";
import { FwdRollMutableRowModel } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/FwdRollMutableRowModel";
import { FwdRollPortfolioModel } from "app/bfx-blotter-intervention/fwd-roll-pricing/models/FwdRollPortfolioModel";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { ZERO } from "shared/base-components/number-spinner.component";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { CcyLookupService, CcyPairLookupService } from "shared/cubiq-services";
import { FwdRollPricingCmd, FwdRollPricingCmdPayload } from "./fwd-roll-pricing-cmd-payload.model";
import { COL_ADJUSTED_PTS_PERCENT_CHANGE } from "shared/cubiq-services/model/FwdRollBaseTableDefinitionFactory";

const logger = LoggerService.for(__dirname);

interface NetSortingModel extends FwdRollMutableRowModel {
  spotAggregateFg: boolean;
  settleDate: CubiqTS.Moment;
  netFixedAmt: CubiqTS.BigNumber;
}

const MUTABLE_COL_NAMES = [
  "marketForwardPts",
  "clientForwardPts",
  "fwdMarkup",
  "marketSpotRate",
  "spotMarkup",
  "clientSpotRate",
  "marketForwardRate",
  "clientForwardRate",
  "intent",
  "netIntent",
  "lockedForUserPricing",
  "netCounterAmt",
  "netFixedAmt",
  "netUsdEquivAmt",
  "selection",
  "fixedAmt",
  "usdEquivAmt",
  "counterAmt",
];

/**
 * View model for a single Fwd Roll pricing component. This is associated one and onle one view but
 * there may be multiple instances.
 * As a view may have its DI Event replaced, all operations on this model should take a portfolioId to ensure that any async
 * operations are still operating on the portfolio that they were intended for.
 */
export class FwdRollPricingModel implements FwdRollPortfolioModel {
  public interfaceCmdEventSource = new BehaviorSubject<FwdRollPricingCmdPayload>(
    new FwdRollPricingCmdPayload(FwdRollPricingCmd.START),
  );
  public interfaceCmd$ = this.interfaceCmdEventSource.asObservable();
  public warning: string = null;
  public disabled: boolean;
  public isSaved: boolean;

  public diEventId: string;
  public portfolioId: string;

  public title: string;

  private baseData: CubiqTS.FwdRollPricingData;

  /** Have we finished with the data? If true, then we can reinitialise the model */
  public isComplete: boolean;

  public rootElement: ElementRef;

  private orderAllocations: FwdRollAllocationModel[];
  private netCurrencies: FwdRollCurrencyModel[];
  private netDealtPairs: FwdRollDealtPairModel[];

  /** True when an update has been made and potential derived changes are pending */
  public isLockActive: boolean = false;

  /** Is this a BM roll? If false, then this is for Algos or other... */
  public isBMRoll: boolean = true;

  public forwardFirstOrder = false;
  public reqForwardPts: CubiqTS.BigNumber = null;
  public failedAdjCalculation: boolean = false;

  private pendingChanges: CubiqTS.FwdRollPricingDataUpdate[][] = [];

  private modelChangeSource = new Subject<CubiqTS.FwdRollChangeRequest>();
  public modelChangeCmd$ = this.modelChangeSource.asObservable();

  private changeBuffer: FwdRollChangeBuffer;
  private changeBufferSubscriber: Subscription;

  private rowMap: Map<string, FwdRollMutableRowModel> = new Map();

  /** Is a full reset pending - if so, then we need to hard fail next error to prevent loop */
  public errorResetPending: boolean = false;

  /** Is a price refresh pending - if so, then we need to update the prime values and  */
  public refreshPending: boolean = false;

  /** Atempt to reset on error failed - can only unlock */
  public errorResetFailed: boolean = false;

  private _isAlgo: boolean = false;

  constructor(
    protected ccyPairService: CcyPairLookupService,
    protected _ccyLookupService: CcyLookupService,
  ) {
    this.orderAllocations = [];
    this.netCurrencies = [];
    this.netDealtPairs = [];
  }

  /**
   * Create local view models from the pricing daa and ensure all local state is initialised
   * @param fwdRollPricingData
   */
  public initialise(fwdRollPricingData: CubiqTS.FwdRollPricingDataImpl) {
    this.diEventId = fwdRollPricingData.diEventId;
    this.portfolioId = fwdRollPricingData.portfolioId;
    this.isComplete = false;
    this.disabled = false;
    this.isLockActive = false;
    this.errorResetPending = false;
    this.errorResetFailed = false;
    this.pendingChanges = [];
    this.rowMap = new Map();
    this.isSaved = false;
    this.isBMRoll = fwdRollPricingData.fixingCd !== null;
    this.forwardFirstOrder = Array.isArray(fwdRollPricingData?.orderAllocations)
      ? fwdRollPricingData?.orderAllocations.some((allocation) => {
          return allocation?.forwardFirstOrder === true;
        })
      : false;

    this._isAlgo = false;
    this.baseData = fwdRollPricingData;
    this.updateTitle(fwdRollPricingData);

    this.initialiseRowModels(fwdRollPricingData);

    if (this.changeBufferSubscriber) {
      this.changeBufferSubscriber.unsubscribe();
    }
    this.changeBuffer?.reset();

    this.changeBuffer = new FwdRollChangeBuffer();
    this.changeBufferSubscriber = this.changeBuffer.rowChanges$.subscribe((changes) =>
      this.pushModelUpdates(changes),
    );

    this.interfaceCmdEventSource.next(new FwdRollPricingCmdPayload(FwdRollPricingCmd.START));
    this.interfaceCmdEventSource.next(new FwdRollPricingCmdPayload(FwdRollPricingCmd.UPDATE_TITLE));
  }

  /**
   * Convert the models we get from the server to new models augmented with update methods and
   * const lookups
   * @param fwdRollPricingData
   */
  private initialiseRowModels(fwdRollPricingData: CubiqTS.FwdRollPricingDataImpl) {
    const augAllocations = [];
    for (const allocation of fwdRollPricingData.orderAllocations) {
      const augAllocation = this.createAllocationModel(allocation);
      augAllocations.push(augAllocation);
    }
    augAllocations.sort(this.compareOrderModels.bind(this));
    this.orderAllocations = augAllocations;

    const augNetCurrencies = [];
    for (const netCurrency of fwdRollPricingData.netCurrencyPositions) {
      const augCurrencyPosition = this.createCurrencyModel(netCurrency);
      augNetCurrencies.push(augCurrencyPosition);
    }
    augNetCurrencies.sort(this.compareNetPositions.bind(this));
    this.netCurrencies = augNetCurrencies;

    const augNetDealtPairs = [];
    for (const netDealtPair of fwdRollPricingData.netDealtPairPositions) {
      const augDealtPairPosition = this.createDealtPairModel(netDealtPair);
      augNetDealtPairs.push(augDealtPairPosition);
    }
    augNetDealtPairs.sort(this.compareNetPositions.bind(this));
    this.netDealtPairs = augNetDealtPairs;
  }

  /**
   * Create a view model for a Dealt Pair row - called on init and potentially on update
   * @param netDealtPair
   * @returns
   */
  private createDealtPairModel(netDealtPair: CubiqTS.FwdRollDealtPairNetPricing) {
    const augDealtPairPosition = new FwdRollDealtPairModel(this);
    Object.assign(augDealtPairPosition, netDealtPair);
    augDealtPairPosition.primedClientForwardPts = augDealtPairPosition.clientForwardPts ?? ZERO;
    augDealtPairPosition.primedMarketForwardPts = augDealtPairPosition.marketForwardPts ?? ZERO;
    augDealtPairPosition.primedFwdMarkup = augDealtPairPosition.fwdMarkup ?? ZERO;
    this.setupPairConstants(augDealtPairPosition);

    this.rowMap.set(augDealtPairPosition.rowId, augDealtPairPosition);
    return augDealtPairPosition;
  }

  /**
   * Create a view model for a Currency row- called on init and potentially on update
   * @param netDealtPair
   * @returns
   */
  private createCurrencyModel(netCurrency: CubiqTS.FwdRollCurrencyNetPricing) {
    const augCurrencyPosition = new FwdRollCurrencyModel(this);
    Object.assign(augCurrencyPosition, netCurrency);
    augCurrencyPosition.primedClientForwardPts = augCurrencyPosition.clientForwardPts ?? ZERO;
    augCurrencyPosition.primedMarketForwardPts = augCurrencyPosition.marketForwardPts ?? ZERO;
    augCurrencyPosition.primedFwdMarkup = augCurrencyPosition.fwdMarkup ?? ZERO;
    this.setupPairConstants(augCurrencyPosition);

    this.rowMap.set(augCurrencyPosition.rowId, augCurrencyPosition);
    return augCurrencyPosition;
  }

  /**
   * Create a view model for a Currency row- called on init only
   * @param netDealtPair
   * @returns
   */
  private createAllocationModel(allocation: CubiqTS.FwdRollOrderAllocationPricing) {
    const augAllocation = new FwdRollAllocationModel(this);
    Object.assign(augAllocation, allocation);
    augAllocation.primedClientForwardPts = augAllocation.clientForwardPts ?? ZERO;
    augAllocation.primedMarketForwardPts = augAllocation.marketForwardPts ?? ZERO;
    augAllocation.primedFwdMarkup = augAllocation.fwdMarkup ?? ZERO;

    this.setupPairConstants(augAllocation);

    this.rowMap.set(augAllocation.rowId, augAllocation);
    return augAllocation;
  }

  /**
   * Comparator for Order allocation rows - puts Spot orders to the bottom then by pair, date
   * amount, and allocation ID
   * @param model1
   * @param model2
   * @returns
   */
  private compareOrderModels(
    model1: FwdRollAllocationModel,
    model2: FwdRollAllocationModel,
  ): number {
    if (
      (model1.settleTenor == CubiqTS.Tenor.SPOT || model2.settleTenor == CubiqTS.Tenor.SPOT) &&
      model1.settleTenor !== model2.settleTenor
    ) {
      if (model1.settleTenor == CubiqTS.Tenor.SPOT) {
        return 1;
      }
      return -1;
    }
    if (model1.baseCcy != model2.baseCcy) {
      return this.compareStrings(model1.baseCcy, model2.baseCcy);
    }
    if (model1.nonBaseCcy != model2.nonBaseCcy) {
      return this.compareStrings(model1.nonBaseCcy, model2.nonBaseCcy);
    }

    const t1 = model1.settleDate?.unix();
    const t2 = model2.settleDate?.unix();
    if (t1 != t2) {
      if (t1 && t2) {
        return t1 - t2;
      }
      return t1 ? -1 : 1;
    }

    if (!model1.fixedAmt.isEqualTo(model2.fixedAmt)) {
      return model1.fixedAmt.isLessThan(model2.fixedAmt) ? -1 : 1;
    }
    return model1.allocationId?.toNumber() - model2.allocationId?.toNumber();
  }

  /**
   * Comparator for Net currency and dealt-pair rows
   * @param model1
   * @param model2
   * @returns
   */
  private compareNetPositions(model1: NetSortingModel, model2: NetSortingModel): number {
    if (model1.baseCcy != model2.baseCcy) {
      return this.compareStrings(model1.baseCcy, model2.baseCcy);
    }
    if (model1.nonBaseCcy != model2.nonBaseCcy) {
      return this.compareStrings(model1.nonBaseCcy, model2.nonBaseCcy);
    }

    if (model1.spotAggregateFg) {
      return -1;
    }
    if (model2.spotAggregateFg) {
      return 1;
    }
    const t1 = model1.settleDate?.unix();
    const t2 = model2.settleDate?.unix();
    if (t1 != t2) {
      if (t1 && t2) {
        return t1 - t2;
      }
      return t1 ? -1 : 1;
    }
    if (model1.lockedForUserPricing != model2.lockedForUserPricing) {
      if (!model1.lockedForUserPricing) {
        return -1;
      }
      if (!model2.lockedForUserPricing) {
        return 1;
      }
    }

    if (!model1.netFixedAmt.isEqualTo(model2.netFixedAmt)) {
      return model1.netFixedAmt.isLessThan(model2.netFixedAmt) ? -1 : 1;
    }
    return this.compareStrings(model1.rowId, model2.rowId);
  }

  private compareStrings(str1: string, str2: string): number {
    if (str1 == null) {
      str1 = "";
    }
    if (str2 == null) {
      str2 = "";
    }
    return str1.localeCompare(str2);
  }

  /**
   * Assocatie the correct precisions with a single data row
   * @param fwdRollModel
   */
  private setupPairConstants(fwdRollModel: FwdRollMutableRowModel): void {
    const pair = this.ccyPairService?.getCcyPair(fwdRollModel.baseCcy, fwdRollModel.nonBaseCcy);
    let decs;
    let ptsPrecision;
    let minPrecision;

    if (pair) {
      const market = pair.base == fwdRollModel.baseCcy;
      if (market) {
        decs = pair.fwdPipsQty;
        ptsPrecision = pair.fwdRateDecimalsQty - pair.fwdPipsQty;
        minPrecision = pair.rateDecimalsQty;
      } else {
        decs = pair.fwdInvertPipsQty;
        ptsPrecision = pair.fwdInvertDecimalsQty - pair.fwdInvertPipsQty;
        minPrecision = pair.spotInvertDecimalsQty;
      }
    } else {
      logger.out.warn("Cannot find pair details:" + fwdRollModel.baseCcy + fwdRollModel.nonBaseCcy);
      decs = 6;
      ptsPrecision = 2;
      minPrecision = 4;
    }
    fwdRollModel.ptsMultiplier = new CubiqTS.BigNumber(`1e-${decs}`);
    fwdRollModel.ptsPrecision = ptsPrecision;
    fwdRollModel.minPrecision = minPrecision;
  }

  public reset() {
    this.portfolioId = null;
    this.diEventId = null;
    this.isComplete = false;
    this.disabled = false;
    this.isLockActive = false;

    this.baseData = null;
    this.rootElement = null;
  }

  /**
   * Setup the title for the window based on the Pricingdata
   * @param pricingData
   */
  private updateTitle(pricingData: CubiqTS.FwdRollPricingData) {
    let rollDescription;
    if (this.isBMRoll) {
      rollDescription = this.getFixingDescription(pricingData);
    } else {
      rollDescription = this.getAlgoDescription(pricingData);
    }
    this.title = `Forward Roll - ${rollDescription} - Portfolio ${pricingData.portfolioId}`;
  }

  /**
   * Title specific for BM portfolios
   * @param pricingData
   * @returns
   */
  private getFixingDescription(pricingData: CubiqTS.FwdRollPricingData) {
    let fixingString = "-";
    if (pricingData) {
      let fixingDate = pricingData.fixingDts.format("DD-MMM-YY");
      let fixingTime = pricingData.fixingDts.format("HH:mm");
      fixingString = `${pricingData.fixingCd} ${fixingTime} ${fixingDate}`;
    }
    return fixingString;
  }

  /**
   * Title specic to Algo portfolios
   * @param pricingData
   * @returns
   */
  private getAlgoDescription(pricingData: CubiqTS.FwdRollPricingData) {
    this._isAlgo = true;

    let algoStr = "-";
    if (pricingData && pricingData.orderAllocations?.length == 1) {
      const order = pricingData.orderAllocations[0];
      algoStr = "Algo Order " + order.orderRef;
    } else {
      logger.out.warn("Cannot determine Algo ID from pricing data");
    }
    return algoStr;
  }

  public get orderCount() {
    return this.baseData?.orderAllocations?.length ?? 0;
  }

  public get lastSaveUserId(): CubiqTS.Long {
    return this.baseData.lastSaveUserId;
  }

  public get lastSaveUserCode(): string {
    return this.baseData.lastSaveUserCode;
  }

  public get lastSaveDate(): CubiqTS.Moment {
    return this.baseData.lastSaveDts;
  }

  public get ordersData() {
    return this.orderAllocations;
  }

  public get currenciesData() {
    return this.netCurrencies;
  }

  public get dealtPairsData() {
    return this.netDealtPairs;
  }

  public get isAlgo() {
    return this._isAlgo;
  }

  /**
   * Is any portfolio in the active state?
   * @param portfolioId If specified, will only check if specified portfolio is active
   * @returns
   */
  public isActive(portfolioId: string = null): boolean {
    if (this.isComplete || this.portfolioId == null) {
      return false;
    }
    return portfolioId == null || portfolioId == this.portfolioId;
  }

  /**
   * is pricing data valid and ready for submission?
   */
  public validatePricing() {
    return true;
  }

  /**
   * Reture a current pricing request based off current order models - normally called by the service to get the price
   * for a requested portfolio
   *
   * @returns
   */
  public getPricingRequest() {
    const retRequest = new CubiqTS.PortfolioForwardPriceRequestImpl();

    retRequest.portfolioId = this.portfolioId;

    retRequest.orderAllocations = this.orderAllocations.concat();
    return retRequest;
  }

  /**
   * Reture a current saving request based off current order models - normally called by the service to save price
   * for a requested portfolio
   *
   * @returns
   */
  public getSaveRequest() {
    const retRequest = new CubiqTS.FwdRollSaveRequestImpl();

    retRequest.portfolioId = this.portfolioId;

    return retRequest;
  }

  public selectionChanged(_selectedRows: any[]) {
    //
  }

  /**
   * Called by the service when a portfolio pricing has finished
   * @param portfolioId
   */
  public completePricing(portfolioId: string) {
    // Don;t check complete as portfolio may be unlocked before pricing completes
    if (portfolioId == this.portfolioId) {
      this.disabled = true;
      this.isComplete = true;
      this.interfaceCmdEventSource.next(
        new FwdRollPricingCmdPayload(FwdRollPricingCmd.PRICING_SUCCESS),
      );
    }
  }

  /**
   * Called by the service after portfolio is unlocked from local call
   * @param portfolioId
   */
  public unlockSuccess(portfolioId: string) {
    if (this.isActive(portfolioId)) {
      this.disabled = true;
      this.isComplete = true;
      this.interfaceCmdEventSource.next(
        new FwdRollPricingCmdPayload(FwdRollPricingCmd.UNLOCK_SUCCESS),
      );
    }
  }

  /**
   * Called by the service when portfolio has failed to unlock
   * @param portfolioId
   * @param reason
   */
  public unlockFail(portfolioId: string, reason) {
    if (this.isActive(portfolioId)) {
      this.interfaceCmdEventSource.next(
        new FwdRollPricingCmdPayload(FwdRollPricingCmd.UNLOCK_FAIL, reason),
      );
    }
  }

  /**
   * Called by service whne portfio has been unlocked externally
   * @param portfolioId
   * @param userStr
   */
  public portfolioUnlocked(portfolioId: string, userStr: string) {
    if (this.isActive(portfolioId)) {
      // We may be unlcoking an aborted portfolio so we don't want any displayed error
      // message to be overwritten by the Unlock message
      if (this.errorResetFailed) {
        this.isComplete = true;
        this.interfaceCmdEventSource.next(
          new FwdRollPricingCmdPayload(FwdRollPricingCmd.UNLOCK_SUCCESS),
        );
      } else {
        this.disabled = true;
        this.isComplete = true;
        this.interfaceCmdEventSource.next(
          new FwdRollPricingCmdPayload(FwdRollPricingCmd.PORTFOLIO_UNLOCKED, userStr),
        );
      }
    }
  }

  /**
   * Called by the service when prices have been saved successfully
   * @param portfolioId
   * @param lastSaveUserId
   * @param lastSaveDts
   */
  public updateSaveDetails(
    portfolioId: string,
    lastSaveUserId: CubiqTS.Long,
    lastSaveDts: CubiqTS.Moment,
  ) {
    if (this.isActive(portfolioId)) {
      this.baseData.lastSaveDts = lastSaveDts;
      this.baseData.lastSaveUserId = lastSaveUserId;
      this.isSaved = true;
      this.interfaceCmdEventSource.next(
        new FwdRollPricingCmdPayload(FwdRollPricingCmd.PRICES_SAVED),
      );
    }
  }

  /**
   * Where service has to present a dialog, this will give the root element of the associated view, if
   * it exists
   * @returns
   */
  public getDocument() {
    if (this.rootElement?.nativeElement) {
      return DomHandler.getBody(this.rootElement.nativeElement);
    }
    return null;
  }

  /**
   * Update the market fwd pts for a given row and propagate changes
   * @param row
   * @param value
   */
  public updateMarketForwardPts(row: FwdRollMutableRowModel, value: CubiqTS.BigNumber): boolean {
    if (value) {
      const dirtyCols: string[] = [];
      const newPoints = value.times(row.ptsMultiplier);

      const diff = newPoints.minus(row.marketForwardPts ?? ZERO);
      if (!diff.isZero()) {
        row.marketForwardPts = newPoints;

        // If it exists we add, otherwise assign
        row.clientForwardPts = row.clientForwardPts?.plus(diff) ?? diff;
        row.marketForwardRate = row.marketForwardRate?.plus(diff) ?? diff;
        row.clientForwardRate = row.clientForwardRate?.plus(diff) ?? diff;

        row.isMarketPtsChanged = !row.marketForwardPts.isEqualTo(row.primedMarketForwardPts);
        row.isClientPtsChanged = !row.clientForwardPts.isEqualTo(row.primedClientForwardPts);

        row.lockedForUserPricing = true;
        const counterName = row.recalcCounterAmount();
        if (dirtyCols) {
          dirtyCols.push(
            "selection",
            "clientForwardPts",
            "marketForwardRate",
            "clientForwardRate",
            counterName,
          );
          if (row?.forwardFirstOrder) {
            dirtyCols.push(COL_ADJUSTED_PTS_PERCENT_CHANGE);
          }
        }
        this.interfaceCmdEventSource.next(
          new FwdRollPricingCmdPayload(
            FwdRollPricingCmd.ROWS_UPDATED,
            {
              updatedRows: [{ row: row, columnNames: dirtyCols }],
            },
            row.rowType,
          ),
        );
        this.notifyUpdate(row);
        return true;
      }
    }
    return false;
  }

  /**
   * Update the client fwd pts for a given row and propagate changes
   * @param row
   * @param value
   */
  public updateClientForwardPts(row: FwdRollMutableRowModel, value: CubiqTS.BigNumber): boolean {
    if (value) {
      const dirtyCols: string[] = [];
      const newPoints = value.times(row.ptsMultiplier);
      const diff = newPoints.minus(row.clientForwardPts ?? ZERO);
      if (!diff.isZero()) {
        row.clientForwardPts = newPoints;

        // if (!this.isAlgo) {
        const additive =
          (row.intent == CubiqTS.OrderIntent.BUY && row.baseCcy === row.fixedCcy) ||
          (row.intent == CubiqTS.OrderIntent.SELL && row.baseCcy !== row.fixedCcy);
        if (additive) {
          row.fwdMarkup = row.fwdMarkup?.plus(diff) ?? diff;
        } else {
          row.fwdMarkup = row.fwdMarkup?.minus(diff) ?? diff;
        }
        // } else {
        //   row.marketForwardPts = newPoints;
        //   row.isMarketPtsChanged = !row.marketForwardPts.isEqualTo(row.primedMarketForwardPts);
        // }

        row.clientForwardRate = row.clientForwardRate?.plus(diff) ?? diff;

        row.isFwdMarkupChanged = !row.fwdMarkup.isEqualTo(row.primedFwdMarkup);
        row.isClientPtsChanged = !row.clientForwardPts.isEqualTo(row.primedClientForwardPts);

        row.lockedForUserPricing = true;
        const counterName = row.recalcCounterAmount();
        if (dirtyCols) {
          dirtyCols.push("selection", "clientForwardRate", "fwdMarkup", counterName);
        }
        this.interfaceCmdEventSource.next(
          new FwdRollPricingCmdPayload(
            FwdRollPricingCmd.ROWS_UPDATED,
            {
              updatedRows: [{ row: row, columnNames: dirtyCols }],
            },
            row.rowType,
          ),
        );
        this.notifyUpdate(row);
        return true;
      }
    }
    return false;
  }

  /**
   * Update the fwd markup for a given row and propagate changes
   * @param row
   * @param value
   */
  public updateForwardMarkup(row: FwdRollMutableRowModel, value: CubiqTS.BigNumber): boolean {
    if (value) {
      const dirtyCols: string[] = [];
      const newPoints = value.times(row.ptsMultiplier);
      const diff = newPoints.minus(row.fwdMarkup ?? ZERO);
      if (!diff.isZero()) {
        row.fwdMarkup = newPoints;
        const additive =
          (row.intent == CubiqTS.OrderIntent.BUY && row.baseCcy === row.fixedCcy) ||
          (row.intent == CubiqTS.OrderIntent.SELL && row.baseCcy !== row.fixedCcy);
        if (additive) {
          row.clientForwardPts = row.clientForwardPts?.plus(diff) ?? diff;
        } else {
          row.clientForwardPts = row.clientForwardPts?.minus(diff) ?? diff;
        }
        row.clientForwardRate = row.clientForwardRate?.plus(diff) ?? diff;

        row.isFwdMarkupChanged = !row.fwdMarkup.isEqualTo(row.primedFwdMarkup);
        row.isClientPtsChanged = !row.clientForwardPts.isEqualTo(row.primedClientForwardPts);

        row.lockedForUserPricing = true;
        const counterName = row.recalcCounterAmount();
        if (dirtyCols) {
          dirtyCols.push("selection", "clientForwardPts", "clientForwardRate", counterName);
        }
        this.interfaceCmdEventSource.next(
          new FwdRollPricingCmdPayload(
            FwdRollPricingCmd.ROWS_UPDATED,
            {
              updatedRows: [{ row: row, columnNames: dirtyCols }],
            },
            row.rowType,
          ),
        );
        this.notifyUpdate(row);
        return true;
      }
    }
    return false;
  }

  /********** Methods for synching client/server update changes */

  /**
   * Aftter local update, lock rows and add change to the buffer
   * @param row
   */
  private notifyUpdate(row: FwdRollMutableRowModel) {
    this.lockRowTypesForEditing(row);
    this.changeBuffer.processModelUpdate(row);
  }

  /**
   * Public interface method (called by service) to apply the changes associated with oen or more
   * model changes update notifications.
   * @param changes
   */
  public applyModelChanges(portfolioId: string, changes: CubiqTS.FwdRollPricingDataUpdate[]) {
    if (this.isActive(portfolioId) && changes) {
      if (this.errorResetPending) {
        this.errorResetPending = false;
        this.processRefresh(changes);
      } else {
        this.pendingChanges.push(changes);

        //Let the buffer know it can output requests again
        this.changeBuffer.outputEnabled();
        if (!this.changeBuffer.hasPendingUpdates()) {
          this.applyAllPendingModelChanges();
        }
      }
    }
  }

  /**
   * If an error occured during a server update then we must resynchronise the local model with the model on the
   * server
   */
  public processUpdateError(portfolioId: string, reason: string) {
    if (this.isActive(portfolioId)) {
      this.disabled = true;
      if (this.errorResetPending) {
        // We cannot do a reset as this is already a response to the last reset - instead, we have to abort
        this.isLockActive = false;
        this.errorResetFailed = true;
        this.interfaceCmdEventSource.next(
          new FwdRollPricingCmdPayload(FwdRollPricingCmd.PORTFOLIO_REFRESH_ERROR, reason),
        );
      } else {
        const request: CubiqTS.FwdRollChangeRequestImpl = new CubiqTS.FwdRollChangeRequestImpl();
        request.portfolioId = this.portfolioId;
        request.resetFwdChanges = true;
        request.changes = [];

        this.errorResetPending = true;
        this.pendingChanges.length = 0;
        this.changeBuffer.reset();

        this.modelChangeSource.next(request);
      }
    }
  }

  public refresh(): void {
    const request: CubiqTS.FwdRollChangeRequestImpl = new CubiqTS.FwdRollChangeRequestImpl();
    request.portfolioId = this.portfolioId;
    request.refreshPoints = true;
    request.changes = [];

    this.refreshPending = true;
    this.modelChangeSource.next(request);
  }

  /**
   * After getting in a list of rows representing entire server sttate, we need to reapply them in
   * this portfolio model
   * @param changes
   */
  private processRefresh(changes: CubiqTS.FwdRollPricingDataUpdate[]) {
    const orders = [];
    const currencies = [];
    const dealtPairs = [];
    for (const change of changes) {
      switch (change.rowType) {
        case CubiqTS.FwdRollRowType.ORDER:
          orders.push(change.model);
          break;
        case CubiqTS.FwdRollRowType.DEALT:
          dealtPairs.push(change.model);
          break;
        case CubiqTS.FwdRollRowType.CURRENCY:
          currencies.push(change.model);
          break;
      }
    }
    // copy the existing map so we can reinstate the priming values
    const oldRows: Map<string, FwdRollMutableRowModel> = Object.assign(new Map(), this.rowMap);

    const fwdRollPricingData = Object.assign(new CubiqTS.FwdRollPricingDataImpl(), this.baseData);
    fwdRollPricingData.netCurrencyPositions = currencies;
    fwdRollPricingData.netDealtPairPositions = dealtPairs;
    fwdRollPricingData.orderAllocations = orders;
    this.initialise(fwdRollPricingData);

    // Now that we've recreated the model, let's put back the original prices
    for (const model of oldRows.values()) {
      const existingRow = this.rowMap.get(model.rowId);
      if (existingRow) {
        this.updateChangeFlagsOnRow(existingRow, true);
      }
    }
  }

  /**
   * Set the price change flags for the row
   * @param model
   */
  private updateChangeFlagsOnRow(model: FwdRollMutableRowModel, reset: boolean) {
    if (reset) {
      // We've just updated the prices on this row from the server so we reset the primed values
      model.primedClientForwardPts = model.clientForwardPts ?? ZERO;
      model.primedFwdMarkup = model.fwdMarkup ?? ZERO;
      model.primedMarketForwardPts = model.marketForwardPts ?? ZERO;
      // For Currency model, need to reenable the strikethrough
      if ((model as any).hasOwnProperty("disableMissingPoints")) {
        (model as any).disableMissingPoints = false;
      }
    }

    if (model.primedClientForwardPts && model.clientForwardPts) {
      model.isClientPtsChanged = !model.primedClientForwardPts.isEqualTo(model.clientForwardPts);
    } else {
      model.isClientPtsChanged = model.clientForwardPts != null;
    }
    if (model.primedFwdMarkup && model.fwdMarkup) {
      model.isFwdMarkupChanged = !model.primedFwdMarkup.isEqualTo(model.fwdMarkup);
    } else {
      model.isFwdMarkupChanged = model.fwdMarkup != null;
    }
    if (model.primedMarketForwardPts && model.marketForwardPts) {
      model.isMarketPtsChanged = !model.primedMarketForwardPts.isEqualTo(model.marketForwardPts);
    } else {
      model.isMarketPtsChanged = model.marketForwardPts != null;
    }
  }

  /**
   * Handle a new set of buffered changes hat need to be sent to the server
   * @param updates
   */
  private pushModelUpdates(updates: CubiqTS.FwdRollChangeImpl[]) {
    const request: CubiqTS.FwdRollChangeRequestImpl = new CubiqTS.FwdRollChangeRequestImpl();
    request.portfolioId = this.portfolioId;
    request.changes = updates;
    this.modelChangeSource.next(request);
  }

  /**
   * Once we received changes form the last change we sent out, we can synchronise our local lodel witht the
   * server changes
   */
  private applyAllPendingModelChanges(): void {
    let changeCount = 0;
    // We assume that all updates are in correct order - oherwise server could have wrong data
    for (const changes of this.pendingChanges) {
      for (const change of changes) {
        changeCount++;
        // apply single change
        switch (change.operation) {
          case CubiqTS.FwdRollUpdateOperation.ADD:
            this.addModelRow(change);
            break;
          case CubiqTS.FwdRollUpdateOperation.DELETE:
            this.deleteModelRow(change);
            break;
          case CubiqTS.FwdRollUpdateOperation.UPDATE:
            this.updateModelRow(change);
            break;
        }
      }
    }
    logger.out.info(
      `Applying server changes - ${this.pendingChanges.length} responses with ${changeCount} updates`,
    );

    this.pendingChanges.length = 0;
    this.refreshPending = false;
    this.unlockRowTypesForEditing();
  }

  /**
   * Update a single row from a server change
   * @param change
   */
  private updateModelRow(change: CubiqTS.FwdRollPricingDataUpdate) {
    const existingRow = this.rowMap.get(change.model.rowId);

    if (existingRow) {
      logger.out.info("Updating pricing row " + change.model.rowId);
      // const newModel = Object.create(existingRow);
      Object.assign(existingRow, change.model);
      this.updateChangeFlagsOnRow(existingRow, this.refreshPending);
      const updatedCols = [
        ...MUTABLE_COL_NAMES,
        ...(existingRow?.forwardFirstOrder ? [COL_ADJUSTED_PTS_PERCENT_CHANGE] : []),
      ];

      this.interfaceCmdEventSource.next(
        new FwdRollPricingCmdPayload(
          FwdRollPricingCmd.SERVER_ROW_UPDATED,
          {
            updatedRows: [{ row: existingRow, columnNames: updatedCols }],
          },
          existingRow.rowType,
        ),
      );
    } else {
      logger.out.error(
        "Got update for a row ID that does not exist in the UI model: " + change.model.rowId,
      );
    }
  }

  /**
   * Delete a single row from a server change
   * @param change
   */
  private deleteModelRow(change: CubiqTS.FwdRollPricingDataUpdate) {
    const existingRow = this.rowMap.get(change.model.rowId);

    if (existingRow) {
      logger.out.info("Deleting pricing row: " + change.model.rowId);
      this.rowMap.delete(change.model.rowId);
      let modelSource: FwdRollMutableRowModel[];
      switch (change.rowType) {
        case CubiqTS.FwdRollRowType.ORDER:
          modelSource = this.orderAllocations;
          break;
        case CubiqTS.FwdRollRowType.DEALT:
          modelSource = this.netDealtPairs;
          break;
        case CubiqTS.FwdRollRowType.CURRENCY:
          modelSource = this.netCurrencies;
          break;
      }
      if (modelSource) {
        const index = modelSource.indexOf(existingRow);
        if (index != -1) {
          logger.out.info("Removing row from model");
          modelSource.splice(index, 1);

          this.interfaceCmdEventSource.next(
            new FwdRollPricingCmdPayload(
              FwdRollPricingCmd.SERVER_ROW_DELETED,
              null,
              existingRow.rowType,
            ),
          );
        } else {
          logger.out.warn("Row for deletion not found");
        }
      }
    }
  }

  /**
   * Add a single row from a server change
   * @param change
   */
  private addModelRow(change: CubiqTS.FwdRollPricingDataUpdate) {
    const existingRow = this.rowMap.get(change.model.rowId);
    if (!existingRow) {
      logger.out.info("Adding new pricing row: " + change.model.rowId);
      let model: NetSortingModel;
      let modelSource: NetSortingModel[];
      switch (change.rowType) {
        case CubiqTS.FwdRollRowType.DEALT:
          modelSource = this.netDealtPairs;
          model = this.createDealtPairModel(change.model as CubiqTS.FwdRollDealtPairNetPricing);
          break;
        case CubiqTS.FwdRollRowType.CURRENCY:
          modelSource = this.netCurrencies;
          model = this.createCurrencyModel(change.model as CubiqTS.FwdRollCurrencyNetPricing);
          break;
      }
      if (model) {
        const insertIndex = this.getSummaryInsertionIndex(model, modelSource);
        modelSource.splice(insertIndex, 0, model);
        this.interfaceCmdEventSource.next(
          new FwdRollPricingCmdPayload(FwdRollPricingCmd.SERVER_ROW_ADDED, null, change.rowType),
        );
      }
    } else {
      logger.out.warn("Row for pricing insertion already found: " + change.model.rowId);
    }
  }

  /**
   * Dettermine the index at which to insert a new row for a summary collection
   * @param model
   * @param modelSource
   * @returns
   */
  private getSummaryInsertionIndex(model: NetSortingModel, modelSource: NetSortingModel[]): number {
    if (modelSource.length == 0) {
      return 0;
    }
    const modelComparator = this.compareNetPositions.bind(this);
    for (let i = 0; i < modelSource.length; i++) {
      if (modelComparator(model, modelSource[i]) == -1) {
        return i;
      }
    }
    return modelSource.length;
  }

  /**
   * When editing a single row mark all other row types as locked
   * @param row
   */
  private lockRowTypesForEditing(row: FwdRollMutableRowModel): void {
    if (!this.isLockActive) {
      this.isLockActive = true;
      const rowType = row.rowType;
      if (rowType != CubiqTS.FwdRollRowType.ORDER) {
        this.orderAllocations.forEach((row) => (row.isEditorDisabled = true));
      }
      if (rowType != CubiqTS.FwdRollRowType.DEALT) {
        this.netDealtPairs.forEach((row) => (row.isEditorDisabled = true));
      }
      if (rowType != CubiqTS.FwdRollRowType.CURRENCY) {
        this.netCurrencies.forEach((row) => (row.isEditorDisabled = true));
      }
      this.interfaceCmdEventSource.next(
        new FwdRollPricingCmdPayload(FwdRollPricingCmd.EDITOR_DISABLED_CHANGED),
      );
    }
  }

  /**
   * Unlock all rows dfor editing - normally called when pending derived updates for the latest
   * edit have been received and processed
   */
  private unlockRowTypesForEditing(): void {
    if (this.isLockActive) {
      this.isLockActive = false;
      this.orderAllocations.forEach((row) => (row.isEditorDisabled = false));
      this.netDealtPairs.forEach((row) => (row.isEditorDisabled = false));
      this.netCurrencies.forEach((row) => (row.isEditorDisabled = false));
      this.interfaceCmdEventSource.next(
        new FwdRollPricingCmdPayload(FwdRollPricingCmd.EDITOR_DISABLED_CHANGED),
      );
    }
  }

  /**
   * Has the user made any changes to anything in the portfolio
   */
  public hasChanges() {
    // If we've had a refresh error, then we ignore  all changes
    const isAbort = this.errorResetPending && this.disabled;

    // NB AJB Really only need to check allocation here
    return (
      !isAbort &&
      !this.isComplete &&
      !this.isSaved &&
      (this.orderAllocations.some((model) => model.isChanged) ||
        this.netCurrencies.some((model) => model.isChanged) ||
        this.netDealtPairs.some((model) => model.isChanged))
    );
  }

  /**
   * Set lockedForPricing on specified rows - if unlocking a ForwardFirst row, then this will emit
   * a confirmation action instead
   * @param row
   * @param locked
   */
  public confirmLockedForUserPricing(row: FwdRollDealtPairModel, locked: boolean) {
    if (row) {
      if (!locked && row.forwardFirstOrder) {
        this.interfaceCmdEventSource.next(
          new FwdRollPricingCmdPayload(
            FwdRollPricingCmd.REQUEST_FORWARD_FIRST_UNLOCK,
            { row, portfolioId: this.portfolioId },
            row.rowType,
          ),
        );
      } else {
        this.setLockedForUserPricing(this.portfolioId, row, locked);
      }
    }
  }

  /**
   * Set lockedForPricing on specified rows without confirmation and send to the ChangeBuffer. Takes a portfolio
   * Id as calls to this may be asynchronous and portfolio may be changed
   * @param portfolioId
   * @param row
   * @param locked
   */
  public setLockedForUserPricing(portfolioId: string, row: FwdRollDealtPairModel, locked: boolean) {
    if (row && this.portfolioId == portfolioId) {
      row.lockedForUserPricing = locked;
      this.changeBuffer.processModelUpdate(row);
      this.interfaceCmdEventSource.next(
        new FwdRollPricingCmdPayload(FwdRollPricingCmd.LOCKED_STATE_CHANGED, null, row.rowType),
      );
    }
  }
}
